import { LessonColorScheme, LessonIconScheme } from '@onesource/schemas';
import type { ComponentType, SVGProps } from 'react';
import { MdDescription, MdFileCopy, MdMenuBook, MdVideocam } from 'react-icons/md';

export type LessonIconComponent = ComponentType<SVGProps<SVGSVGElement>>;

export interface LessonColorSchemeConfig {
  /** Human-readable label for selectors (dashboard form). */
  label: string;
  /** Card background color. */
  bgcolor: string;
  /** Icon color that stays legible on top of `bgcolor`. */
  iconColor: string;
}

export interface LessonIconSchemeConfig {
  /** Human-readable label for selectors (dashboard form). */
  label: string;
  /** SVG icon component (uses `currentColor`, so the consumer sets the color). */
  Icon: LessonIconComponent;
}

/**
 * Single source of truth for lesson/section color schemes.
 * Used by the dashboard form (label + swatch) and the members public view
 * (bgcolor + iconColor). Order here is the order shown in selectors.
 */
export const LESSON_COLOR_SCHEMES: Record<LessonColorScheme, LessonColorSchemeConfig> = {
  [LessonColorScheme.GREEN]: { label: 'Green (Sage)', bgcolor: '#b7c6bb', iconColor: '#FFE9DB' },
  [LessonColorScheme.CORAL]: { label: 'Coral', bgcolor: 'rgba(255,114,98,0.78)', iconColor: '#FFE9DB' },
  [LessonColorScheme.PEACH]: { label: 'Peach', bgcolor: '#ffe9db', iconColor: '#FF073B' },
  [LessonColorScheme.BLUE]: { label: 'Blue (Sky)', bgcolor: '#b8daee', iconColor: '#1F5C7A' },
  [LessonColorScheme.LIGHT_SAGE_GREEN]: { label: 'Light Sage Green', bgcolor: '#D0DDD3', iconColor: '#1F5C7A' },
  [LessonColorScheme.ICE_BLUE]: { label: 'Ice Blue', bgcolor: '#E2F4FF', iconColor: '#1F5C7A' },
  [LessonColorScheme.ROSE_TAUPE]: { label: 'Rose Taupe', bgcolor: '#D6B9AA', iconColor: '#1F5C7A' },
  [LessonColorScheme.MINT_CREAM]: { label: 'Mint Cream', bgcolor: '#EAF3EC', iconColor: '#1F5C7A' },
  [LessonColorScheme.CORAL_RED]: { label: 'Coral Red', bgcolor: '#FF7262', iconColor: '#FFE9DB' },
  [LessonColorScheme.PEACH_CREAM]: { label: 'Peach Cream', bgcolor: '#FFE9DB', iconColor: '#FF073B' },
  [LessonColorScheme.DUSTY_BLUE]: { label: 'Dusty Blue', bgcolor: '#89A9BB', iconColor: '#FFE9DB' },
};

/**
 * Single source of truth for lesson/section icon schemes.
 * Used by the dashboard form (label + icon) and the members public view (icon).
 */
const BookIcon: LessonIconComponent = props => <MdMenuBook {...props} />;
const VideoCameraIcon: LessonIconComponent = props => <MdVideocam {...props} />;
const FilesIcon: LessonIconComponent = props => <MdFileCopy {...props} />;
const DocumentIcon: LessonIconComponent = props => <MdDescription {...props} />;

export const LESSON_ICON_SCHEMES: Record<LessonIconScheme, LessonIconSchemeConfig> = {
  [LessonIconScheme.BOOK]: { label: 'Book', Icon: BookIcon },
  [LessonIconScheme.VIDEO]: { label: 'Video', Icon: VideoCameraIcon },
  [LessonIconScheme.FILES]: { label: 'Files', Icon: FilesIcon },
  [LessonIconScheme.DOCUMENT]: { label: 'Document', Icon: DocumentIcon },
};

/** Ordered color-scheme options for selectors, derived from the source map. */
export const LESSON_COLOR_SCHEME_OPTIONS = (Object.keys(LESSON_COLOR_SCHEMES) as LessonColorScheme[]).map(value => ({
  value,
  ...LESSON_COLOR_SCHEMES[value],
}));

/** Ordered icon-scheme options for selectors, derived from the source map. */
export const LESSON_ICON_SCHEME_OPTIONS = (Object.keys(LESSON_ICON_SCHEMES) as LessonIconScheme[]).map(value => ({
  value,
  ...LESSON_ICON_SCHEMES[value],
}));

/**
 * Resolve a lesson/section color scheme to its config, falling back to GREEN
 * when the value is missing or not a known scheme.
 */
export const getLessonColorScheme = (colorScheme?: LessonColorScheme | string | null): LessonColorSchemeConfig =>
  LESSON_COLOR_SCHEMES[colorScheme as LessonColorScheme] ?? LESSON_COLOR_SCHEMES[LessonColorScheme.GREEN];

/**
 * Resolve a lesson/section icon scheme to its icon component, falling back to
 * BOOK when the value is missing or not a known scheme.
 */
export const getLessonIconScheme = (iconScheme?: LessonIconScheme | string | null): LessonIconComponent =>
  (LESSON_ICON_SCHEMES[iconScheme as LessonIconScheme] ?? LESSON_ICON_SCHEMES[LessonIconScheme.BOOK]).Icon;
